import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';

import { mapProps } from '../../../../../../../util/hocs';
import { getAlbumMembershipGroupQuery } from '../../../../../../temp/views/memberships/AlbumMembershipGroup/graphql/queries/getAlbumMembershipGroup';
import {
  albumMembershipGroupSupportsLegacyPrintPurchasing,
  createAlbumMembershipGroupEntity,
  getAlbumMembershipGroupAlbumPrintPackages,
  getAlbumMembershipGroupPhotoPricing,
} from '../PhotoPurchasingOptions/helpers';
import {
  getCheckoutCart,
  getCheckoutCartTotalItems,
  hasPrintPackageWithDownloadsIncluded as hasPrintPackageWithDownloadsIncludedSelector,
} from '../../../../../../../services/checkout/redux/checkoutSelectors';
import {
  cartItemUpdate,
  toggleAlbumCustomPrintCartItem,
  toggleAlbumCustomPrintSubscriptionBundle,
  toggleAlbumFolderSubscriptionCartItem,
  toggleAlbumPrintPackageCartItem,
  toggleAlbumPrintPackageSubscriptionBundle,
  toggleAlbumSubscriptionCartItem,
  toggleDigitalBundleCartItem,
  toggleDigitalDownloadCartItem,
  toggleIndividualPrintCartItem,
  togglePhotoDownloadCartItem,
  togglePhotoPricingPrintProductCartItem,
  togglePrintProductCartItem,
} from '../../../../../../../services/checkout/redux/checkoutActions';
import {
  getAlbumCustomPrintCartItem,
  getAlbumFolderSubscriptionCartItem,
  getAlbumPrintPackageCartItem,
  getAlbumSubscriptionCartItem,
  getDigitalBundleCartItem,
  getDigitalDownloadCartItem,
  getPrintProductCartItem,
} from '../../../../../../../services/checkout/redux/helpers';
import PurchasingOptionsPhotoPreview from './components/PurchasingOptionsPhotoPreview';
import DismissButton from '../../../../../../../components/global/DismissButton';
import Button from '../../../../../../../components/global/Button';
import AlbumPrintPackageOption from './components/AlbumPrintPackageOption';
import ShoppingBasketIcon from '../../../../../../../components/icons/ShoppingBasketIcon';
import Badge from '../../../../../../../components/global/Badge';
import CART_ITEM from '../../../../../../../services/checkout/constants/cartItems';
import {
  getAlbumMembershipGroupAlbumCustomPrints,
  getAlbumMembershipGroupBundleAlbumCustomPrint,
  getAlbumMembershipGroupDiscountedAlbumPrintPackages,
  getAlbumMembershipGroupIdentityByIdentityId,
  getAlbumMembershipGroupSubscriptionAlbum,
  getAlbumMembershipGroupSubscriptionAlbumFolder,
  albumMembershipGroupSupportsAlbumCustomPrintPurchasing,
  hasSubscriptionInCart,
  getAlbumMembershipGroupAccountCardAlbumCustomPrints,
  getAlbumMembershipGroupEventCollages,
  getAlbumMembershipGroupSpecialtyAlbumCustomPrints,
  getAlbumMembershipGroupDigitalBundles,
  getAlbumMembershipGroupGreenScreenStatus,
  albumMembershipGroupHasPurchasingOptions,
  shouldShowDigitalsAndCustomPrintsFirst,
} from './helpers';
import AlbumCustomPrintOption from './components/AlbumCustomPrintOption';
import AlbumFolderSubscriptionOption from './components/AlbumFolderSubscriptionOption';
import AlbumSubscriptionOption from './components/AlbumSubscriptionOption';
import DigitalDownloadOption from './components/DigitalDownloadOption';
import IndividualPrintsOption from './components/IndividualPrintsOption';
import AlbumCustomPrintSubscriptionBundleOption from './components/AlbumCustomPrintSubscriptionBundleOption';
import AlbumPrintPackageSubscriptionBundleOption from './components/AlbumPrintPackageSubscriptionBundleOption';
import AccountCardAlbumCustomPrintOption from './components/AccountCardAlbumCustomPrintOption';
import WaldogramsOption from './components/WaldogramsOption/WaldogramsOption';
import PhotoDownloadOption from '../PhotoPurchasingOptions/components/PhotoDownloadOption';
import { openModal as openModalAction } from '../../../../../../../actions/modal-actions';
import { CHECKOUT_CART_MODAL } from '../../../../../../../services/checkout/components/CheckoutCart/modal';
import PrintProductsOption from './components/PrintProductsOption';
import StandardPrintOption from './components/StandardPrintOption';
import { MeQuery } from '../../../../../../../graphql/queries/account';
import PrintProductDiscountAlert from './components/PrintProductDiscountAlert';
import { getGlobalPrintCategoriesQuery } from '../../../../../../temp/views/memberships/AlbumMembershipGroup/graphql/queries/getGlobalPrintCategories';
import RequiresPrintPackageAlertModal from './components/RequiresPrintPackageAlert';
import getPrintProducts from '../../../../../../../services/cards/graphql/queries/getPrintProducts';
import SubscriptionIdentitySelectModal from '../../../All/components/AllPhotos/components/SubscriptionIdentitySelectModal';
import EventCollagesOption from './components/EventCollagesOption';
import {
  getAlbumMembershipGroupEntityAccount,
  isMultiPosePackageMembership,
  isPictureDayMembership,
} from '../../../../../../temp/views/memberships/AlbumMembershipGroup/helpers';
import { albumSupportsWaldograms } from '../../../../../../web-gallery/utilities/waldograms';
import getPrintPartners from '../../../../../../../services/cards/graphql/queries/getPrintPartners';
import SpecialtyPrintOption from './SpecialtyPrintOption';
import DigitalBundleOption from './components/DigitalBundleOption';
import useIdentityJerseyColors from '../../hooks/useIdentityJerseyColors';
import { extractWatermarkSettings } from '../../../../../../../util/watermark-settings';
import { CUSTOM_PRINT_SUBTYPE } from '../../../../../../../constants/pro-custom-prints';
import AlbumPrintPackageMaxCheckoutItemsOption from './components/AlbumPrintPackageMaxCheckoutItemsOption';
import Spinner from '../../../../../../../components/global/Spinner';

const propTypes = {
  className: PropTypes.string,
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
    search_subscription_discount_percentage: PropTypes.number,
  }),
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  album: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  isPhotoEntitled: PropTypes.bool,
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  standardPrintProducts: PropTypes.shape({
    records: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        price: PropTypes.shape({
          value: PropTypes.number.isRequired,
        }).isRequired,
      }).isRequired,
    ),
  }),
  printCategories: PropTypes.shape({
    records: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        print_products: PropTypes.shape({
          total: PropTypes.number.isRequired,
        }).isRequired,
      }).isRequired,
    ),
  }),
  cart: PropTypes.array.isRequired,
  totalCartItems: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onCartItemUpdate: PropTypes.func.isRequired,
  onAlbumFolderSubscriptionToggle: PropTypes.func.isRequired,
  onAlbumSubscriptionToggle: PropTypes.func.isRequired,
  onAlbumPrintPackageToggle: PropTypes.func.isRequired,
  onDigitalDownloadToggle: PropTypes.func.isRequired,
  onPhotoDownloadToggle: PropTypes.func.isRequired,
  onPrintItemToggle: PropTypes.func.isRequired,
  onAlbumCustomPrintToggle: PropTypes.func.isRequired,
  onAlbumCustomPrintSubscriptionBundleToggle: PropTypes.func.isRequired,
  onAlbumPrintPackageSubscriptionBundleToggle: PropTypes.func.isRequired,
  onPrintProductItemToggle: PropTypes.func.isRequired,
  onPhotoPricingPrintProductToggle: PropTypes.func.isRequired,
  webGalleryPrice: PropTypes.shape({
    value: PropTypes.number,
  }),
  openModal: PropTypes.func.isRequired,
  hasPurchasedPrintPackages: PropTypes.bool,
  hasPrintPackageWithDownloadsIncluded: PropTypes.bool,
  allowIdentitySelection: PropTypes.bool,
  loadingAlbumMembershipGroup: PropTypes.bool,
};

// This view works as a price list when there is no photo available.
//
const PurchasingOptions = ({
  className,
  account,
  identity,
  albumMembershipGroup,
  isPhotoEntitled,
  photo,
  album,
  onClose,
  cart = [],
  totalCartItems,
  standardPrintProducts,
  printCategories,
  onCartItemUpdate,
  onAlbumFolderSubscriptionToggle,
  onAlbumSubscriptionToggle,
  onAlbumPrintPackageToggle,
  onDigitalDownloadToggle,
  onPhotoDownloadToggle,
  onPrintItemToggle,
  onAlbumCustomPrintToggle,
  onAlbumCustomPrintSubscriptionBundleToggle,
  onAlbumPrintPackageSubscriptionBundleToggle,
  onPrintProductItemToggle,
  onDigitalBundleToggle,
  title = 'Add to cart',
  webGalleryPrice,
  openModal,
  onPhotoPricingPrintProductToggle,
  hasPurchasedPrintPackages,
  hasPrintPackageWithDownloadsIncluded,
  allowIdentitySelection,
  loadingAlbumMembershipGroup,
}) => {
  const readOnly = !photo;
  const classes = classNames({
    'purchasing-options-list-wrap': true,
    'purchasing-options-list-pricing-only': readOnly,
    'modal-contained-wrap': true,
    [className]: !!className,
  });

  const { jerseyColors } = useIdentityJerseyColors({
    identityId: identity?.id,
    albumFolderId:
      albumMembershipGroup?.album_folder?.id ??
      albumMembershipGroup?.album?.root_album_folder?.id,
  });

  if (!albumMembershipGroup && !album) {
    return null;
  }

  const albumMembershipGroupEntity = createAlbumMembershipGroupEntity(
    albumMembershipGroup,
    album,
  );

  const vendorAccount = getAlbumMembershipGroupEntityAccount(
    albumMembershipGroupEntity,
  );

  const { sell_prints_enabled: sellPrintsEnabled = true } =
    vendorAccount?.pro_settings ?? {};

  const showDigitalsAndCustomPrintsFirst =
    shouldShowDigitalsAndCustomPrintsFirst(vendorAccount);

  const watermarkSettings = extractWatermarkSettings(vendorAccount);

  const pricedTag = photo?.price_album_folder_tag;
  const tagAllowsDownload = !pricedTag || pricedTag?.digital_downloads_enabled;
  const isGreenScreenEnabled = getAlbumMembershipGroupGreenScreenStatus(
    albumMembershipGroupEntity,
  );

  const identityIsPurchased =
    getAlbumMembershipGroupIdentityByIdentityId(
      albumMembershipGroupEntity,
      identity?.id,
    )?.has_purchased_subscription ?? false;

  // AlbumFolder-level subscriptions (PPG) are prioritized over Album ones (PPA).
  // Generally we show the user only 1 subscription.
  const subscriptionAlbumFolder =
    getAlbumMembershipGroupSubscriptionAlbumFolder(albumMembershipGroupEntity);
  const subscriptionAlbum = getAlbumMembershipGroupSubscriptionAlbum(
    albumMembershipGroupEntity,
  );

  const subscriptionAvailable =
    !!albumMembershipGroup &&
    (!!subscriptionAlbumFolder || !!subscriptionAlbum) &&
    (!identityIsPurchased || readOnly);

  const albumPrintPackages = getAlbumMembershipGroupAlbumPrintPackages(
    albumMembershipGroupEntity,
    !tagAllowsDownload,
    jerseyColors,
    sellPrintsEnabled,
  );
  // We need the secondary array since not having it would mean
  // the check for hasPrintPackageCartItems below could fail erroneously
  const albumPrintPackagesForCartCheck =
    getAlbumMembershipGroupAlbumPrintPackages(
      albumMembershipGroupEntity,
      !tagAllowsDownload,
      [],
      sellPrintsEnabled,
    );
  const discountedAlbumPrintPackages =
    getAlbumMembershipGroupDiscountedAlbumPrintPackages(
      albumMembershipGroupEntity,
      !tagAllowsDownload,
      sellPrintsEnabled,
    );
  const photoPricing = getAlbumMembershipGroupPhotoPricing(
    albumMembershipGroupEntity,
    sellPrintsEnabled,
  );
  const albumCustomPrints = getAlbumMembershipGroupAlbumCustomPrints(
    albumMembershipGroupEntity,
    jerseyColors,
    sellPrintsEnabled,
  );
  const eventCollages = getAlbumMembershipGroupEventCollages(
    albumMembershipGroupEntity,
    sellPrintsEnabled,
  );
  const accountCardAlbumCustomPrints =
    getAlbumMembershipGroupAccountCardAlbumCustomPrints(
      albumMembershipGroupEntity,
      jerseyColors,
      sellPrintsEnabled,
    );
  const specialtyAlbumCustomPrints =
    getAlbumMembershipGroupSpecialtyAlbumCustomPrints(
      albumMembershipGroupEntity,
      sellPrintsEnabled,
    );
  const sortedAlbumCustomPrints = [
    ...eventCollages,
    ...accountCardAlbumCustomPrints,
    ...specialtyAlbumCustomPrints,
  ].sort((a, b) => {
    if (a.display_order < b.display_order) {
      return -1;
    }

    if (a.display_order > b.display_order) {
      return 1;
    }

    return 0;
  });

  const bundleAlbumCustomPrint = getAlbumMembershipGroupBundleAlbumCustomPrint(
    albumMembershipGroupEntity,
    sellPrintsEnabled,
  );
  const digitalBundles = getAlbumMembershipGroupDigitalBundles(
    albumMembershipGroupEntity,
  );
  const isPictureDay = isPictureDayMembership(albumMembershipGroupEntity);
  const allowMultiPose =
    isMultiPosePackageMembership(albumMembershipGroup) ?? false;
  const supportsWaldograms =
    (albumMembershipGroup?.is_album || !!album) &&
    albumSupportsWaldograms(albumMembershipGroupEntity.album);
  // As of 2022, ANY package is enough to unlock individual pricing
  // settings. Therefore, photo checking is no longer required here.
  const albumPrintPackageCartItems = cart.filter(
    item =>
      [
        CART_ITEM.ALBUM_PRINT_PACKAGE,
        CART_ITEM.PURCHASED_PRINT_PACKAGE,
      ].includes(item.type) &&
      albumPrintPackagesForCartCheck
        .map(({ id }) => id)
        .includes(item.albumPrintPackage.id),
  );
  const hasPrintPackageCartItems = albumPrintPackageCartItems.length > 0;
  const photoPricingRequiresPackage = photoPricing?.requires_package_purchase;
  const canPurchaseIndividual =
    !photoPricingRequiresPackage ||
    hasPrintPackageCartItems ||
    hasPurchasedPrintPackages;

  const canPurchaseSpecialtyPrint = specialtyPrint => {
    const specialtyPrintRequiresPackage =
      specialtyPrint?.custom_print?.requires_package_purchase;

    return (
      !specialtyPrintRequiresPackage ||
      hasPrintPackageCartItems ||
      hasPurchasedPrintPackages
    );
  };

  const printItems = photoPricing?.print_items?.records ?? [];
  const photoPricingPrintProducts =
    photoPricing?.print_product_items?.records ?? [];
  const supportsLegacyPrints =
    albumMembershipGroupSupportsLegacyPrintPurchasing(
      albumMembershipGroupEntity,
      sellPrintsEnabled,
    );
  const subscriptionInCart = hasSubscriptionInCart({
    cart,
    album: subscriptionAlbum,
    albumFolder: subscriptionAlbumFolder,
    identity,
  });
  const hasPhotoPricingPrintItems =
    printItems?.length > 0 || photoPricingPrintProducts.length > 0;

  // Overall this is a reliable way to determine allPhotos, as having
  // a membership will always take priority (show matches) while having
  // no membership + an album means we come from All Photos.
  const isAllPhotos = !albumMembershipGroup && !!album;

  const shouldShowAlbumFolderSubscription =
    identity && subscriptionAlbumFolder && (!identityIsPurchased || readOnly);

  const shouldShowAlbumSubscription =
    identity &&
    !!albumMembershipGroup &&
    subscriptionAlbum &&
    !subscriptionAlbumFolder &&
    (!identityIsPurchased || readOnly);

  const shouldShowDigitalBundles =
    digitalBundles.length > 0 && tagAllowsDownload;

  const shouldShowIndividualDownloads =
    photoPricing?.digital_downloads_price?.value > 0 &&
    !isPhotoEntitled &&
    tagAllowsDownload;

  const hierarchyItemId = albumMembershipGroup?.is_album
    ? albumMembershipGroup.album.root_album_folder?.id
    : (albumMembershipGroup?.id ?? album?.root_album_folder?.id);

  const hasPurchasingOptionsBool = albumMembershipGroupHasPurchasingOptions({
    albumMembershipGroup: albumMembershipGroupEntity,
    identity,
    sellPrintsEnabled,
  });

  const onCheckout = () => {
    openModal(CHECKOUT_CART_MODAL);
    onClose();
  };

  const SpecialtyPrintsFragment = (
    <Fragment>
      {(albumCustomPrints?.length > 0 ||
        accountCardAlbumCustomPrints?.length > 0 ||
        specialtyAlbumCustomPrints?.length > 0) && (
        <h4 className="mt-2">Specialty Products</h4>
      )}
      {albumMembershipGroupSupportsAlbumCustomPrintPurchasing(
        albumMembershipGroupEntity,
      ) &&
        albumCustomPrints.map(albumCustomPrint => {
          return (
            <AlbumCustomPrintOption
              key={albumCustomPrint.id}
              readOnly={readOnly}
              albumCustomPrint={albumCustomPrint}
              onAlbumCustomPrintToggle={configuration =>
                onAlbumCustomPrintToggle({
                  albumCustomPrint,
                  identity,
                  albumMembershipGroup: albumMembershipGroupEntity,
                  subscribed: identityIsPurchased,
                  // TODO @consumer [cart] "Shipping" here should be determined by the actual print (if it requires or not)
                  shipping: true,
                  configuration,
                })
              }
              identity={identity}
              cartItem={getAlbumCustomPrintCartItem(cart, {
                albumCustomPrintId: albumCustomPrint.id,
                identityId: identity?.id,
              })}
              hasPurchasedSubscription={identityIsPurchased}
              subscriptionAvailable={subscriptionAvailable}
              hasSubscriptionInCart={subscriptionInCart}
              watermarkSettings={watermarkSettings}
            />
          );
        })}

      {albumMembershipGroupSupportsAlbumCustomPrintPurchasing(
        albumMembershipGroupEntity,
      ) &&
        sortedAlbumCustomPrints.map(albumCustomPrint => {
          if (
            albumCustomPrint?.custom_print?.subtype ===
            CUSTOM_PRINT_SUBTYPE.COLLAGE
          ) {
            return (
              <EventCollagesOption
                photo={photo}
                readOnly={readOnly}
                eventCollages={eventCollages}
                identity={identity}
                albumMembershipGroup={albumMembershipGroupEntity}
                disabled={!canPurchaseIndividual}
                isAllPhotos={isAllPhotos}
                onCheckout={onCheckout}
                onAlbumCustomPrintToggle={(eventCollage, configuration) =>
                  onAlbumCustomPrintToggle({
                    albumCustomPrint: eventCollage,
                    identity,
                    albumMembershipGroup: albumMembershipGroupEntity,
                    subscribed: identityIsPurchased,
                    // TODO @consumer [cart] "Shipping" here should be determined by the actual print (if it requires or not)
                    shipping: true,
                    configuration,
                    photoPricing,
                    photo,
                    isAllPhotos,
                    forceEntitlement: !isPictureDay,
                  })
                }
              />
            );
          }

          if (
            albumCustomPrint?.custom_print?.subtype ===
            CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT
          ) {
            return (
              <SpecialtyPrintOption
                key={albumCustomPrint.id}
                readOnly={readOnly}
                disabled={!canPurchaseSpecialtyPrint(albumCustomPrint)}
                disabledElement={
                  <span className="purchasing-option-item-info-requires-package mb-1">
                    Requires the purchase of a print package first.
                  </span>
                }
                photo={photo}
                specialtyPrint={albumCustomPrint}
                onSpecialtyPrintToggle={({
                  photos: selectedPhotos = [],
                } = {}) => {
                  let photos = [{ photo, position: 0 }];

                  if (selectedPhotos.length > 0) {
                    photos = selectedPhotos.map((selectedPhoto, index) => ({
                      photo: selectedPhoto,
                      position: index,
                    }));
                  }

                  onAlbumCustomPrintToggle({
                    albumCustomPrint,
                    identity,
                    photo,
                    albumMembershipGroup: albumMembershipGroupEntity,
                    subscribed: identityIsPurchased,
                    shipping: false,
                    configuration: {
                      photos,
                    },
                  });
                }}
                identity={identity}
                cartItem={getAlbumCustomPrintCartItem(cart, {
                  albumCustomPrintId: albumCustomPrint.id,
                  identityId: identity?.id,
                })}
                hasPurchasedSubscription={identityIsPurchased}
                subscriptionAvailable={subscriptionAvailable}
                hasSubscriptionInCart={subscriptionInCart}
                albumMembershipGroup={albumMembershipGroupEntity}
                isAllPhotos={isAllPhotos}
                watermarkSettings={watermarkSettings}
              />
            );
          }

          return (
            <AccountCardAlbumCustomPrintOption
              key={albumCustomPrint.id}
              readOnly={readOnly}
              albumCustomPrint={albumCustomPrint}
              onAlbumCustomPrintToggle={configuration =>
                onAlbumCustomPrintToggle({
                  albumCustomPrint,
                  identity,
                  albumMembershipGroup: albumMembershipGroupEntity,
                  subscribed: identityIsPurchased,
                  // TODO @consumer [cart] "Shipping" here should be determined by the actual print (if it requires or not)
                  shipping: true,
                  configuration,
                  photoPricing,
                  photo,
                  isAllPhotos,
                })
              }
              identity={identity}
              cartItem={getAlbumCustomPrintCartItem(cart, {
                albumCustomPrintId: albumCustomPrint.id,
                identityId: identity?.id,
              })}
              hasPurchasedSubscription={identityIsPurchased}
              subscriptionAvailable={subscriptionAvailable}
              hasSubscriptionInCart={subscriptionInCart}
              albumMembershipGroup={albumMembershipGroupEntity}
              disabled={!canPurchaseIndividual}
              isAllPhotos={isAllPhotos}
              disabledElement={
                <span className="purchasing-option-item-info-requires-package">
                  Requires the purchase of a print package first.
                </span>
              }
              watermarkSettings={watermarkSettings}
              photo={photo}
            />
          );
        })}

      {supportsWaldograms && (
        <WaldogramsOption
          albumMembershipGroup={albumMembershipGroupEntity}
          disabled={!canPurchaseIndividual}
          readOnly={readOnly}
          onClose={onClose}
        />
      )}
    </Fragment>
  );

  const DigitalsFragment = (
    <Fragment>
      {(shouldShowAlbumFolderSubscription ||
        shouldShowAlbumSubscription ||
        shouldShowDigitalBundles ||
        shouldShowIndividualDownloads) && (
        <h4 className="mt-2">Digital Only</h4>
      )}

      {shouldShowAlbumFolderSubscription && (
        <AlbumFolderSubscriptionOption
          readOnly={readOnly}
          albumFolder={subscriptionAlbumFolder}
          cartItem={getAlbumFolderSubscriptionCartItem(cart, {
            albumFolderId: subscriptionAlbumFolder?.id,
            identityId: identity?.id,
          })}
          identity={identity}
          allowIdentitySelection={allowIdentitySelection}
          onSubscriptionToggle={({ selectedIdentity } = {}) =>
            onAlbumFolderSubscriptionToggle({
              albumMembershipGroup: albumMembershipGroupEntity,
              albumFolder: subscriptionAlbumFolder,
              identity: selectedIdentity ?? identity,
              hierarchyItemId,
              isGreenScreenEnabled,
            })
          }
          disabled={!albumMembershipGroup}
        />
      )}

      {shouldShowAlbumSubscription && (
        <AlbumSubscriptionOption
          readOnly={readOnly}
          album={subscriptionAlbum}
          cartItem={getAlbumSubscriptionCartItem(cart, {
            albumId: subscriptionAlbum?.id,
            identityId: identity?.id,
          })}
          identity={identity}
          allowIdentitySelection={allowIdentitySelection}
          onSubscriptionToggle={({ selectedIdentity } = {}) =>
            onAlbumSubscriptionToggle({
              albumMembershipGroup: albumMembershipGroupEntity,
              album: subscriptionAlbum,
              identity: selectedIdentity ?? identity,
              hierarchyItemId,
              isGreenScreenEnabled,
            })
          }
          disabled={!albumMembershipGroup}
        />
      )}

      {shouldShowDigitalBundles && (
        <Fragment>
          {digitalBundles.map(digitalBundle => (
            <DigitalBundleOption
              readOnly={readOnly}
              identity={identity}
              photo={photo}
              digitalBundle={digitalBundle}
              isPhotoEntitled={isPhotoEntitled}
              albumMembershipGroup={albumMembershipGroupEntity}
              isAllPhotos={isAllPhotos}
              cartItem={getDigitalBundleCartItem(cart, {
                digitalBundleId: digitalBundle.id,
                identityId: identity.id,
                photosCount: digitalBundle?.photos_count,
                photoId: photo?.id,
              })}
              disabled={
                subscriptionInCart ||
                (digitalBundle.requires_package_purchase &&
                  !(hasPrintPackageCartItems || hasPurchasedPrintPackages))
              }
              disabledBySubscription={subscriptionInCart}
              onDigitalBundleToggle={({ photos } = {}) =>
                onDigitalBundleToggle({
                  albumMembershipGroup: albumMembershipGroupEntity,
                  identity,
                  digitalBundle,
                  photos,
                  hierarchyItemId,
                  isGreenScreenEnabled,
                })
              }
              watermarkSettings={watermarkSettings}
            />
          ))}
        </Fragment>
      )}

      {shouldShowIndividualDownloads && (
        <DigitalDownloadOption
          readOnly={readOnly}
          photoPricing={photoPricing}
          photo={photo}
          cartItem={getDigitalDownloadCartItem(cart, {
            photoId: photo?.id,
          })}
          onDigitalDownloadToggle={() =>
            onDigitalDownloadToggle({
              albumMembershipGroup: albumMembershipGroupEntity,
              photo,
              album,
              photoPricing,
              hierarchyItemId,
              isGreenScreenEnabled,
            })
          }
          disabled={subscriptionInCart || !canPurchaseIndividual}
          disabledBySubscription={subscriptionInCart}
          included={hasPrintPackageWithDownloadsIncluded}
        />
      )}

      {webGalleryPrice?.value > 0 && !isPhotoEntitled && (
        <PhotoDownloadOption
          photo={photo}
          album={album}
          webGalleryPrice={webGalleryPrice}
          disabled={!canPurchaseIndividual}
          cart={cart}
          readOnly={readOnly}
          onPhotoDownloadToggle={onPhotoDownloadToggle}
          included={hasPrintPackageWithDownloadsIncluded}
        />
      )}
    </Fragment>
  );

  return (
    <div className={classes}>
      <div className="purchasing-options-list-header modal-contained-header">
        <span className="modal-contained-title">{title}</span>
        <DismissButton className="ml-auto" onClick={onClose} />
      </div>

      {photo && (
        <PurchasingOptionsPhotoPreview
          photo={photo}
          watermarkSettings={watermarkSettings}
        />
      )}

      <div className="modal-contained-content">
        <div className="purchasing-options-list-contents">
          {/* This is only intended for while the queries reload. Realistically if we are here, we have options. */}
          {!hasPurchasingOptionsBool &&
            (albumMembershipGroup || loadingAlbumMembershipGroup) && (
              <div className="text-center my-2">
                <Spinner />
              </div>
            )}
          {showDigitalsAndCustomPrintsFirst && (
            <Fragment>
              {DigitalsFragment}
              {SpecialtyPrintsFragment}
            </Fragment>
          )}
          {albumPrintPackages?.length > 0 && (
            <h4 className="mt-2">Print Packages</h4>
          )}
          {supportsLegacyPrints &&
            account?.search_subscription_discount_percentage > 0 && (
              <PrintProductDiscountAlert
                discountPercentage={
                  account.search_subscription_discount_percentage
                }
              />
            )}

          {subscriptionAvailable && bundleAlbumCustomPrint && (
            <AlbumCustomPrintSubscriptionBundleOption
              readOnly={readOnly}
              identity={identity}
              albumCustomPrint={bundleAlbumCustomPrint}
              subscriptionItem={subscriptionAlbum || subscriptionAlbumFolder}
              onBundleToggle={configuration =>
                onAlbumCustomPrintSubscriptionBundleToggle({
                  albumCustomPrint: bundleAlbumCustomPrint,
                  subscriptionItem:
                    subscriptionAlbumFolder || subscriptionAlbum,
                  identity,
                  albumMembershipGroup: albumMembershipGroupEntity,
                  shipping: true,
                  configuration,
                })
              }
              inCart={
                subscriptionInCart &&
                !!getAlbumCustomPrintCartItem(cart, {
                  albumCustomPrintId: bundleAlbumCustomPrint?.id,
                  identityId: identity?.id,
                })
              }
            />
          )}

          {subscriptionAvailable &&
            discountedAlbumPrintPackages.map(albumPrintPackage => {
              const cartItem = getAlbumPrintPackageCartItem(cart, {
                albumPrintPackageId: albumPrintPackage.id,
                photoId: photo?.id,
              });
              const inCart = subscriptionInCart && !!cartItem;

              return (
                <AlbumPrintPackageSubscriptionBundleOption
                  key={albumPrintPackage.id}
                  readOnly={readOnly}
                  albumPrintPackage={albumPrintPackage}
                  subscriptionItem={
                    subscriptionAlbumFolder || subscriptionAlbum
                  }
                  identity={identity}
                  onBundleToggle={() => {
                    onAlbumPrintPackageSubscriptionBundleToggle({
                      albumMembershipGroup: albumMembershipGroupEntity,
                      albumPrintPackage,
                      photo,
                      album,
                      subscriptionItem:
                        subscriptionAlbumFolder || subscriptionAlbum,
                      identity,
                    });
                  }}
                  inCart={inCart}
                />
              );
            })}

          {albumPrintPackages.map(albumPrintPackage => {
            if (albumPrintPackage?.print_package?.max_checkout_items) {
              return (
                <AlbumPrintPackageMaxCheckoutItemsOption
                  readOnly={readOnly}
                  key={albumPrintPackage.id}
                  albumPrintPackage={albumPrintPackage}
                  cartItem={getAlbumPrintPackageCartItem(cart, {
                    albumPrintPackageId: albumPrintPackage.id,
                    photoId: photo?.id,
                  })}
                  onAlbumPrintPackageToggle={({ selectedPrintPackageItems }) =>
                    onAlbumPrintPackageToggle({
                      albumPrintPackage,
                      photo,
                      album,
                      albumMembershipGroup: albumMembershipGroupEntity,
                      isAllPhotos,
                      hierarchyItemId,
                      isGreenScreenEnabled,
                      selectedPrintPackageItems,
                      allowMultiPose,
                    })
                  }
                  onUpdate={onCartItemUpdate}
                  identity={identity}
                  albumMembershipGroup={albumMembershipGroupEntity}
                  photo={photo}
                  onCheckout={onCheckout}
                  isAllPhotos={isAllPhotos}
                  isGreenScreenEnabled={isGreenScreenEnabled}
                  watermarkSettings={watermarkSettings}
                />
              );
            }

            return (
              <AlbumPrintPackageOption
                readOnly={readOnly}
                key={albumPrintPackage.id}
                albumPrintPackage={albumPrintPackage}
                cartItem={getAlbumPrintPackageCartItem(cart, {
                  albumPrintPackageId: albumPrintPackage.id,
                  photoId: photo?.id,
                })}
                onAlbumPrintPackageToggle={() =>
                  onAlbumPrintPackageToggle({
                    albumPrintPackage,
                    photo,
                    album,
                    albumMembershipGroup: albumMembershipGroupEntity,
                    isAllPhotos,
                    hierarchyItemId,
                    isGreenScreenEnabled,
                    allowMultiPose,
                  })
                }
                onUpdate={onCartItemUpdate}
                identity={identity}
                albumMembershipGroup={albumMembershipGroupEntity}
                photo={photo}
                onCheckout={onCheckout}
                isAllPhotos={isAllPhotos}
                isGreenScreenEnabled={isGreenScreenEnabled}
                watermarkSettings={watermarkSettings}
              />
            );
          })}
          {!showDigitalsAndCustomPrintsFirst && (
            <Fragment>
              {SpecialtyPrintsFragment}
              {DigitalsFragment}
            </Fragment>
          )}
          {!loadingAlbumMembershipGroup &&
            (hasPhotoPricingPrintItems ||
              (!isPictureDay &&
                (supportsLegacyPrints || isPhotoEntitled) &&
                sellPrintsEnabled)) && (
              <h4 className="mt-2">Individual Prints &amp; Products</h4>
            )}
          {hasPhotoPricingPrintItems && (
            <IndividualPrintsOption
              readOnly={readOnly}
              cart={cart}
              photo={photo}
              album={album}
              photoPricing={photoPricing}
              onPrintItemToggle={onPrintItemToggle}
              onPrintItemUpdate={onCartItemUpdate}
              onPrintProductToggle={({
                accountCard,
                roesConfiguration,
                photoPricingPrintProduct,
              }) => {
                onPhotoPricingPrintProductToggle({
                  photo,
                  identity,
                  photoPricingPrintProduct,
                  photoPricing,
                  accountCard,
                  roesConfiguration,
                  album,
                  albumMembershipGroup: albumMembershipGroupEntity,
                });
              }}
              onPrintProductUpdate={onCartItemUpdate}
              onCheckout={onCheckout}
              albumMembershipGroup={albumMembershipGroupEntity}
              identity={identity}
              disabled={!canPurchaseIndividual}
              disabledElement={
                <span className="purchasing-option-item-info-requires-package">
                  Requires the purchase of a print package first.
                </span>
              }
              watermarkSettings={watermarkSettings}
              isAllPhotos={isAllPhotos}
            />
          )}

          {!loadingAlbumMembershipGroup &&
            !isPictureDay &&
            (supportsLegacyPrints || isPhotoEntitled) &&
            sellPrintsEnabled &&
            // Avoid competing against the owner of the gallery
            !hasPhotoPricingPrintItems && (
              <>
                {standardPrintProducts?.records?.length > 0 && (
                  <StandardPrintOption
                    readOnly={readOnly}
                    cart={cart}
                    photo={photo}
                    standardPrintProducts={standardPrintProducts}
                    onPrintProductItemToggle={configuration => {
                      onPrintProductItemToggle({
                        accountCard: configuration.accountCard,
                        albumCustomPrint: configuration.albumCustomPrint,
                        printProductSize: configuration.printProductSize,
                        photo,
                        identity,
                        albumMembershipGroup: albumMembershipGroupEntity,
                      });
                    }}
                    onCheckout={onCheckout}
                    albumMembershipGroup={albumMembershipGroupEntity}
                    identity={identity}
                    getCartItem={({ printProductSizeId }) =>
                      getPrintProductCartItem(cart, {
                        printProductSizeId,
                        photoId: photo?.id,
                      })
                    }
                    watermarkSettings={watermarkSettings}
                  />
                )}
                {printCategories?.records?.length > 0 && (
                  <PrintProductsOption
                    readOnly={readOnly}
                    cart={cart}
                    photo={photo}
                    printCategories={printCategories}
                    onPrintProductItemToggle={configuration => {
                      onPrintProductItemToggle({
                        accountCard: configuration.accountCard,
                        albumCustomPrint: configuration.albumCustomPrint,
                        printProductSize: configuration.printProductSize,
                        photo,
                        identity,
                        albumMembershipGroup: albumMembershipGroupEntity,
                      });
                    }}
                    onCheckout={onCheckout}
                    albumMembershipGroup={albumMembershipGroupEntity}
                    identity={identity}
                    getCartItem={({ printProductSizeId }) =>
                      getPrintProductCartItem(cart, {
                        printProductSizeId,
                        photoId: photo?.id,
                      })
                    }
                    watermarkSettings={watermarkSettings}
                  />
                )}
              </>
            )}
        </div>
      </div>

      {totalCartItems > 0 && !readOnly && (
        <div className="purchasing-options-list-footer modal-contained-footer">
          <Button btnOutline onClick={onClose}>
            Continue shopping
          </Button>

          <Button btnStyle="success-vibrant" onClick={onCheckout}>
            <span className="purchasing-options-list-btn-shopping-cart-icon">
              <ShoppingBasketIcon />
              <Badge badgeStyle="danger" badgeSize="xs" round>
                {totalCartItems}
              </Badge>
            </span>
            Check out
          </Button>
        </div>
      )}
      <RequiresPrintPackageAlertModal />
      <SubscriptionIdentitySelectModal
        hierarchyItemId={albumMembershipGroup?.id}
        isAlbumSubscription={!subscriptionAlbumFolder && !!subscriptionAlbum}
      />
    </div>
  );
};

PurchasingOptions.propTypes = propTypes;

const mapStateToProps = (state, { photo }) => ({
  cart: getCheckoutCart(state),
  totalCartItems: getCheckoutCartTotalItems(state),
  hasPrintPackageWithDownloadsIncluded:
    hasPrintPackageWithDownloadsIncludedSelector(state, photo),
  identity: state.checkout.activeIdentity,
});

const mapDispatchToProps = {
  onDigitalDownloadToggle: toggleDigitalDownloadCartItem,
  onPhotoDownloadToggle: togglePhotoDownloadCartItem,
  onAlbumFolderSubscriptionToggle: toggleAlbumFolderSubscriptionCartItem,
  onAlbumSubscriptionToggle: toggleAlbumSubscriptionCartItem,
  onAlbumCustomPrintToggle: toggleAlbumCustomPrintCartItem,
  onAlbumCustomPrintSubscriptionBundleToggle:
    toggleAlbumCustomPrintSubscriptionBundle,
  onAlbumPrintPackageSubscriptionBundleToggle:
    toggleAlbumPrintPackageSubscriptionBundle,
  onAlbumPrintPackageToggle: toggleAlbumPrintPackageCartItem,
  onPrintItemToggle: toggleIndividualPrintCartItem,
  onPrintProductItemToggle: togglePrintProductCartItem,
  onPhotoPricingPrintProductToggle: togglePhotoPricingPrintProductCartItem,
  onDigitalBundleToggle: toggleDigitalBundleCartItem,
  onCartItemUpdate: cartItemUpdate,
  openModal: openModalAction,
};

const withMeQuery = graphql(MeQuery, {
  props: ({ data: { me } }) => ({
    account: me,
  }),
});

const withAlbumMembershipGroup = graphql(getAlbumMembershipGroupQuery, {
  options: ({ hierarchyItemId, album, albumFolderId }) => ({
    variables: {
      id: hierarchyItemId,
      album_folder_id: albumFolderId ?? album?.root_album_folder?.id,
    },
    fetchPolicy: 'cache-first',
  }),
  props: ({ data: { albumMembershipGroup, loading } }) => ({
    albumMembershipGroup,
    loadingAlbumMembershipGroup: loading,
  }),
  skip: ({ hierarchyItemId }) => !hierarchyItemId,
});

const withBayPrintPartner = graphql(getPrintPartners, {
  options: () => ({
    variables: {
      name: 'Bay Photo',
    },
  }),
  props: ({ data: { printPartners } }) => ({
    bayPhotosPrintPartner: printPartners?.records?.[0],
  }),
});

const withPrintProducts = graphql(getPrintProducts, {
  options: ({ bayPhotosPrintPartner }) => ({
    variables: {
      name: 'Standard Prints',
      print_partner_id: bayPhotosPrintPartner?.id,
    },
  }),
  props: ({ data: { printProducts: standardPrintProducts } }) => ({
    standardPrintProducts,
  }),
  skip: ({ bayPhotosPrintPartner }) => !bayPhotosPrintPartner,
});

const withPrintCategories = graphql(getGlobalPrintCategoriesQuery, {
  props: ({ data: { printCategories } = {} }) => ({
    printCategories,
  }),
  // Allow for Album / Folder ID here since it's possible we want prints on FWG
  skip: ({ match, hierarchyItemId }) =>
    !hierarchyItemId && !match.params.folderId && !match.params.albumId,
});

export { PurchasingOptions as PurchasingOptionsComponent };

export default compose(
  withRouter,
  mapProps(({ match, hierarchyItemId, ...props }) => ({
    ...props,
    hierarchyItemId: hierarchyItemId || match.params.hierarchyItemId,
    match,
  })),
  connect(mapStateToProps, mapDispatchToProps),
  withMeQuery,
  withAlbumMembershipGroup,
  withBayPrintPartner,
  withPrintProducts,
  withPrintCategories,
)(PurchasingOptions);
